/**
 * AWP Quick Add (Devoción CO)
 *
 * A self-contained collection-card quick-add modal. Clicking a card's "+" trigger
 * opens ONE shared dialog (#awp-quick-add-dialog), which fetches the product JSON
 * (/products/{handle}.js) and renders its own controls — variant option selects,
 * a quantity stepper, and (for subscription-eligible coffee products) a One-time /
 * Subscribe purchase-type selector.
 *
 * Why self-contained instead of cloning the PDP form (like the US theme does):
 * on CO the PDP is assembled from independent blocks (size / grind / quantity /
 * purchase-option) whose inline <script>s don't run from fetched HTML, and the
 * subscription selector is injected at runtime by the external Moxie app widget
 * (subscription-custom), which isn't present in the server HTML at all. So there
 * is no single element to clone.
 *
 * Subscriptions are Moxie's line-item-property contract (NOT a native selling_plan):
 * a normal /cart/add.js with properties[Tipo de compra]=Suscripción, etc. This is
 * the exact recipe coffee-quiz.js already uses successfully — see addToCart() below.
 * The merchant's Moxie Shopify Function reads those properties to apply the discount.
 */

const AWP_QA_DIALOG_ID = 'awp-quick-add-dialog';

/** Subscription discount (matches coffee-quiz.js SUBSCRIPTION_DISCOUNT and the Moxie Function). */
const AWP_QA_SUBSCRIPTION_DISCOUNT = 0.05;

/**
 * Delivery cadence options. `label` must match the Moxie "Frecuencia de entrega"
 * text the PDP/quiz send (Spanish), `weeks` feeds "Cantidad de frecuencia".
 * Mirrors the frequency dropdown in coffee-quiz-screen-results.liquid.
 */
const AWP_QA_FREQUENCIES = [
  { value: '1-week', label: 'Cada semana', weeks: 1 },
  { value: '2-weeks', label: 'Cada 2 semanas', weeks: 2 },
  { value: '3-weeks', label: 'Cada 3 semanas', weeks: 3 },
  { value: '4-weeks', label: 'Cada 4 semanas', weeks: 4 },
  { value: '6-weeks', label: 'Cada 6 semanas', weeks: 6 },
  { value: '8-weeks', label: 'Cada 8 semanas', weeks: 8 },
];
const AWP_QA_DEFAULT_FREQUENCY = '2-weeks';

/**
 * Subscription value props shown as a checklist under the "Suscríbete" title,
 * above the frequency dropdown. Mirrors the PDP subscription callouts and the
 * benefit_* settings on the coffee-quiz results screen.
 */
const AWP_QA_BENEFITS = [
  'Cuchara gratis con tu primer pedido',
  'Cambia o cancela cuando quieras',
  '¡Nunca te quedes sin café!',
];

/** Colombian-peso formatting (no decimals), matching the coffee quiz. */
function awpQaFormatMoney(subunits) {
  const amount = (Number(subunits) || 0) / 100;
  return `$${amount.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/**
 * The "+" control on a product card. Opens the shared dialog for its product.
 */
class AwpQuickAddTrigger extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (this.button) this.button.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    event.preventDefault();
    const dialog = document.getElementById(AWP_QA_DIALOG_ID);
    if (!(dialog instanceof AwpQuickAddDialog)) return;

    const handle = this.dataset.productHandle;
    if (!handle) return;

    const smallHandle = this.dataset.sizeSmallHandle;
    const largeHandle = this.dataset.sizeLargeHandle;

    dialog.openFor({
      handle,
      title: this.dataset.productTitle || '',
      image: this.dataset.productImage || '',
      url: this.dataset.productUrl || `/products/${handle}`,
      subscribable: this.dataset.subscribable === 'true',
      sizes: smallHandle && largeHandle ? { small: smallHandle, large: largeHandle } : null,
    });
  };
}

if (!customElements.get('awp-quick-add-trigger')) {
  customElements.define('awp-quick-add-trigger', AwpQuickAddTrigger);
}

class AwpQuickAddDialog extends HTMLElement {
  /** @type {Map<string, object>} product handle -> product JSON */
  #cache = new Map();
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {object | null} */
  #product = null;
  #subscribable = false;
  /** @type {{ small: string, large: string } | null} sibling product handles for the size toggle */
  #sizes = null;
  #isAdding = false;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.titleEl = this.querySelector('[data-awp-qa-title]');
    this.imageEl = this.querySelector('[data-awp-qa-image]');
    this.imageLinkEl = this.querySelector('[data-awp-qa-image-link]');
    this.priceEl = this.querySelector('[data-awp-qa-price]');
    this.bodyEl = this.querySelector('[data-awp-qa-body]');

    this.querySelector('[data-awp-qa-close]')?.addEventListener('click', () => this.close());
    this.dialog.addEventListener('click', this.#handleBackdropClick);
    this.dialog.addEventListener('close', this.#handleClose);
  }

  /**
   * @param {{ handle: string, title?: string, image?: string, url?: string, subscribable?: boolean }} args
   */
  async openFor({ handle, title = '', image = '', url = '', subscribable = false, sizes = null }) {
    if (!this.dialog) return;

    this.#subscribable = subscribable;
    this.#sizes = sizes && sizes.small && sizes.large ? sizes : null;
    this.#product = null;

    // Prime the header from what the card already knew, then refine on load.
    if (this.titleEl) {
      this.titleEl.textContent = title;
      if (url) this.titleEl.setAttribute('href', url);
    }
    if (this.imageLinkEl && url) this.imageLinkEl.setAttribute('href', url);
    if (this.imageEl instanceof HTMLImageElement) {
      if (image) {
        this.imageEl.src = image;
        this.imageEl.alt = title;
      } else {
        this.imageEl.removeAttribute('src');
      }
    }
    if (this.priceEl) this.priceEl.textContent = '';
    this.#setLoading();

    if (typeof this.dialog.showModal === 'function') {
      if (!this.dialog.open) this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    document.body.style.overflow = 'hidden';

    try {
      const product = await this.#getProduct(handle);
      this.#product = product;
      this.#render(product);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('AWP Quick Add: failed to load product', error);
      this.#setError();
    }
  }

  close() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
      this.#handleClose();
    }
  }

  #handleClose = () => {
    document.body.style.overflow = '';
  };

  #handleBackdropClick = (event) => {
    // Native <dialog> backdrop clicks land on the dialog element itself.
    if (event.target === this.dialog) this.close();
  };

  /** @param {string} handle */
  async #getProduct(handle) {
    const cached = this.#cache.get(handle);
    if (cached) return cached;

    this.#abort?.abort();
    this.#abort = new AbortController();

    const response = await fetch(`/products/${handle}.js`, { signal: this.#abort.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const product = await response.json();
    this.#cache.set(handle, product);
    return product;
  }

  #setLoading() {
    if (!this.bodyEl) return;
    this.bodyEl.innerHTML =
      '<div class="awp-qa__loading"><span class="awp-qa__spinner"></span><span>Cargando…</span></div>';
  }

  #setError() {
    if (!this.bodyEl) return;
    this.bodyEl.innerHTML =
      '<div class="awp-qa__error">No pudimos cargar el producto. Inténtalo de nuevo.</div>';
  }

  /**
   * @param {object} product
   * @param {object} [restore] state to re-apply after a size switch
   *   ({ purchase, frequency, qty, options: { [position]: value } })
   */
  #render(product, restore = null) {
    if (!this.bodyEl) return;

    if (this.titleEl) {
      this.titleEl.textContent = product.title || this.titleEl.textContent;
      if (product.url) this.titleEl.setAttribute('href', product.url);
    }
    if (this.imageLinkEl && product.url) this.imageLinkEl.setAttribute('href', product.url);
    if (this.imageEl instanceof HTMLImageElement) {
      const featured = product.featured_image || product.images?.[0];
      if (featured) {
        this.imageEl.src = featured;
        this.imageEl.alt = product.title || '';
      }
    }

    const currentSize = this.#currentSize(product);
    const sizeMarkup = this.#sizes
      ? `
        <div class="awp-qa__field awp-qa__field--size">
          <label class="awp-qa__label">Presentación</label>
          <div class="awp-qa__sizes" data-awp-qa-sizes>
            <button type="button" class="awp-qa__size${
              currentSize === '340g' ? ' is-selected' : ''
            }" data-awp-qa-size="340g" aria-pressed="${currentSize === '340g'}">340g</button>
            <button type="button" class="awp-qa__size${
              currentSize === '2.5kg' ? ' is-selected' : ''
            }" data-awp-qa-size="2.5kg" aria-pressed="${currentSize === '2.5kg'}">2.5 kg</button>
          </div>
        </div>`
      : '';

    // Normalize options to { name, position, values }. The /products/{handle}.js
    // endpoint may return `options` as objects ({name, position, values}) or as
    // plain name strings depending on the API version — handle both, and derive
    // values from the variants when they aren't provided. Only render a select
    // for options that actually offer a choice (>1 value).
    const options = this.#normalizeOptions(product).filter((opt) => opt.values.length > 1);
    const showSubscribe = this.#subscribable && product.available;

    const optionsMarkup = options
      .map((opt) => {
        const values = opt.values
          .map((v) => `<option value="${awpQaEscape(v)}">${awpQaEscape(v)}</option>`)
          .join('');
        return `
          <div class="awp-qa__field">
            <label class="awp-qa__label" for="awp-qa-opt-${opt.position}">${awpQaEscape(opt.name)}</label>
            <select class="awp-qa__select" id="awp-qa-opt-${opt.position}" data-awp-qa-option="${opt.position}">
              ${values}
            </select>
          </div>`;
      })
      .join('');

    const subscribeMarkup = showSubscribe
      ? `
        <div class="awp-qa__purchase" data-awp-qa-purchase>
          <label class="awp-qa__purchase-option">
            <input type="radio" name="awp-qa-purchase" value="onetime">
            <span class="awp-qa__purchase-label">Compra única</span>
          </label>
          <label class="awp-qa__purchase-option">
            <input type="radio" name="awp-qa-purchase" value="subscribe" checked>
            <span class="awp-qa__purchase-label">Suscríbete y ahorra ${Math.round(
              AWP_QA_SUBSCRIPTION_DISCOUNT * 100
            )}%</span>
          </label>
          <div class="awp-qa__frequency" data-awp-qa-frequency-wrap>
            <ul class="awp-qa__benefits">
              ${AWP_QA_BENEFITS.map((b) => `<li>${awpQaEscape(b)}</li>`).join('')}
            </ul>
            <label class="awp-qa__label" for="awp-qa-frequency">Recibe cada</label>
            <select class="awp-qa__select" id="awp-qa-frequency" data-awp-qa-frequency>
              ${AWP_QA_FREQUENCIES.map(
                (f) =>
                  `<option value="${f.value}"${
                    f.value === AWP_QA_DEFAULT_FREQUENCY ? ' selected' : ''
                  }>${f.label}</option>`
              ).join('')}
            </select>
          </div>
        </div>`
      : '';

    this.bodyEl.innerHTML = `
      <div class="awp-qa__controls">
        ${sizeMarkup}
        ${optionsMarkup}
        <div class="awp-qa__field awp-qa__field--qty">
          <label class="awp-qa__label">Cantidad</label>
          <div class="awp-qa__qty">
            <button type="button" class="awp-qa__qty-btn" data-awp-qa-qty-decrease aria-label="Disminuir cantidad">−</button>
            <input type="text" inputmode="numeric" class="awp-qa__qty-value" value="1" data-awp-qa-qty aria-label="Cantidad">
            <button type="button" class="awp-qa__qty-btn" data-awp-qa-qty-increase aria-label="Aumentar cantidad">+</button>
          </div>
        </div>
      </div>
      ${subscribeMarkup}
      <button type="button" class="awp-qa__submit awp_button button" data-awp-qa-submit>Añadir al carrito</button>
    `;

    this.#bindControls();
    if (restore) this.#applyState(restore);
    this.#syncVariant();
  }

  #bindControls() {
    const body = this.bodyEl;
    if (!body) return;

    body.querySelectorAll('[data-awp-qa-size]').forEach((btn) => {
      btn.addEventListener('click', () => this.#switchSize(btn.dataset.awpQaSize));
    });

    body.querySelectorAll('[data-awp-qa-option]').forEach((sel) => {
      sel.addEventListener('change', () => this.#syncVariant());
    });

    body.querySelector('[data-awp-qa-qty-decrease]')?.addEventListener('click', () => this.#stepQty(-1));
    body.querySelector('[data-awp-qa-qty-increase]')?.addEventListener('click', () => this.#stepQty(1));
    body.querySelector('[data-awp-qa-qty]')?.addEventListener('input', (e) => {
      const el = e.target;
      const cleaned = String(el.value).replace(/[^0-9]/g, '');
      el.value = cleaned;
    });
    body.querySelector('[data-awp-qa-qty]')?.addEventListener('blur', (e) => {
      const el = e.target;
      let val = parseInt(el.value, 10);
      if (!val || val < 1) val = 1;
      el.value = String(val);
    });

    body.querySelectorAll('input[name="awp-qa-purchase"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        const wrap = body.querySelector('[data-awp-qa-frequency-wrap]');
        if (wrap) wrap.hidden = this.#selectedPurchaseType() !== 'subscribe';
      });
    });

    body.querySelector('[data-awp-qa-submit]')?.addEventListener('click', () => this.#addToCart());
  }

  #stepQty(delta) {
    const input = this.bodyEl?.querySelector('[data-awp-qa-qty]');
    if (!input) return;
    let val = (parseInt(input.value, 10) || 1) + delta;
    if (val < 1) val = 1;
    input.value = String(val);
  }

  #currentQty() {
    const input = this.bodyEl?.querySelector('[data-awp-qa-qty]');
    const val = input ? parseInt(input.value, 10) : 1;
    return val && val > 0 ? val : 1;
  }

  #selectedPurchaseType() {
    const checked = this.bodyEl?.querySelector('input[name="awp-qa-purchase"]:checked');
    return checked ? checked.value : 'onetime';
  }

  /** Which size the given (or current) product represents, or null when not a size pair. */
  #currentSize(product) {
    if (!this.#sizes) return null;
    const handle = (product || this.#product)?.handle;
    return handle && handle === this.#sizes.large ? '2.5kg' : '340g';
  }

  /**
   * Swap the modal to the sibling size product (340g <-> 2.5kg), which is a
   * separate product, then re-render preserving the current selections.
   * @param {string} size '340g' | '2.5kg'
   */
  async #switchSize(size) {
    if (!this.#sizes) return;
    const targetHandle = size === '2.5kg' ? this.#sizes.large : this.#sizes.small;
    if (!targetHandle || targetHandle === this.#product?.handle) return;

    const restore = this.#captureState();
    this.#setLoading();
    try {
      const product = await this.#getProduct(targetHandle);
      this.#product = product;
      this.#render(product, restore);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('AWP Quick Add: failed to switch size', error);
      // Keep the modal usable by restoring the product we still have.
      if (this.#product) this.#render(this.#product, restore);
      else this.#setError();
    }
  }

  /** Snapshot the current selections so a size switch doesn't reset them. */
  #captureState() {
    const options = {};
    this.bodyEl?.querySelectorAll('[data-awp-qa-option]').forEach((sel) => {
      options[sel.dataset.awpQaOption] = sel.value;
    });
    return {
      purchase: this.#selectedPurchaseType(),
      frequency: this.#selectedFrequency(),
      qty: this.#currentQty(),
      options,
    };
  }

  /** Re-apply a snapshot from #captureState after a re-render, where the sibling supports it. */
  #applyState(state) {
    const body = this.bodyEl;
    if (!body || !state) return;

    Object.entries(state.options || {}).forEach(([position, value]) => {
      const sel = body.querySelector(`[data-awp-qa-option="${position}"]`);
      if (sel && Array.from(sel.options).some((o) => o.value === value)) sel.value = value;
    });

    const qtyInput = body.querySelector('[data-awp-qa-qty]');
    if (qtyInput && state.qty) qtyInput.value = String(state.qty);

    if (state.purchase === 'onetime') {
      const otRadio = body.querySelector('input[name="awp-qa-purchase"][value="onetime"]');
      if (otRadio) otRadio.checked = true;
    }

    const freqSel = body.querySelector('[data-awp-qa-frequency]');
    if (
      freqSel &&
      state.frequency &&
      Array.from(freqSel.options).some((o) => o.value === state.frequency)
    ) {
      freqSel.value = state.frequency;
    }

    const wrap = body.querySelector('[data-awp-qa-frequency-wrap]');
    if (wrap) wrap.hidden = this.#selectedPurchaseType() !== 'subscribe';
  }

  /**
   * @param {object} product
   * @returns {Array<{ name: string, position: number, values: string[] }>}
   */
  #normalizeOptions(product) {
    const raw = product.options || [];
    const variants = product.variants || [];
    return raw.map((opt, index) => {
      const position = typeof opt === 'object' && opt.position ? opt.position : index + 1;
      const name = typeof opt === 'string' ? opt : opt.name || `Opción ${position}`;
      let values = typeof opt === 'object' && Array.isArray(opt.values) ? opt.values.slice() : [];
      if (values.length === 0) {
        // Derive distinct values from the variants for this position.
        const seen = new Set();
        variants.forEach((v) => {
          const val = v[`option${position}`];
          if (val != null && !seen.has(val)) seen.add(val);
        });
        values = Array.from(seen);
      }
      return { name, position, values };
    });
  }

  /** Resolve the variant from the current option selects. */
  #selectedVariant() {
    if (!this.#product) return null;
    const variants = this.#product.variants || [];
    if (variants.length === 1) return variants[0];

    const selects = Array.from(this.bodyEl?.querySelectorAll('[data-awp-qa-option]') || []);
    if (selects.length === 0) return variants[0];

    const chosen = {};
    selects.forEach((sel) => {
      chosen[parseInt(sel.dataset.awpQaOption, 10)] = sel.value;
    });

    return (
      variants.find((v) =>
        Object.keys(chosen).every((pos) => String(v[`option${pos}`]) === String(chosen[pos]))
      ) || null
    );
  }

  /** Update the header price + submit button state from the selected variant. */
  #syncVariant() {
    const variant = this.#selectedVariant();
    const submit = this.bodyEl?.querySelector('[data-awp-qa-submit]');

    if (this.priceEl) {
      this.priceEl.textContent = variant ? awpQaFormatMoney(variant.price) : '';
    }

    if (submit) {
      if (!variant) {
        submit.disabled = true;
        submit.textContent = 'No disponible';
      } else if (!variant.available) {
        submit.disabled = true;
        submit.textContent = 'Agotado';
      } else {
        submit.disabled = false;
        submit.textContent = 'Añadir al carrito';
      }
    }
  }

  async #addToCart() {
    if (this.#isAdding) return;
    const variant = this.#selectedVariant();
    if (!variant || !variant.available) return;

    this.#isAdding = true;
    const submit = this.bodyEl?.querySelector('[data-awp-qa-submit]');
    if (submit) {
      submit.disabled = true;
      submit.textContent = 'Añadiendo…';
    }

    // Build an application/x-www-form-urlencoded body — this is the exact contract
    // coffee-quiz.js uses. The Moxie subscription app + its Shopify Function read
    // these line-item properties to apply the "Descuento suscripción" discount at
    // checkout; JSON / items-array requests don't trigger the Function reliably.
    const params = new URLSearchParams();
    params.append('id', String(variant.id));
    params.append('quantity', String(this.#currentQty()));
    params.append('selling_plan', '');

    if (this.#selectedPurchaseType() === 'subscribe') {
      const freq =
        AWP_QA_FREQUENCIES.find((f) => f.value === this.#selectedFrequency()) ||
        AWP_QA_FREQUENCIES.find((f) => f.value === AWP_QA_DEFAULT_FREQUENCY);
      const discountPercent = Math.round(AWP_QA_SUBSCRIPTION_DISCOUNT * 100);
      const discountedUnitPrice = Math.round((variant.price / 100) * (1 - AWP_QA_SUBSCRIPTION_DISCOUNT));

      params.append('properties[Tipo de compra]', 'Suscripción');
      params.append('properties[Frecuencia de entrega]', freq.label);
      params.append('properties[Unidad de frecuencia]', 'WEEK');
      params.append('properties[Cantidad de frecuencia]', String(freq.weeks));
      params.append('properties[Suscripción]', `${discountPercent}% OFF`);
      params.append('properties[Precio con descuento]', String(discountedUnitPrice));
      params.append('properties[Descuento aplicado]', `${discountPercent}%`);
    }

    // Ask /cart/add.js to return the re-rendered cart-drawer section(s) so the
    // drawer morphs SYNCHRONOUSLY from HTML that already reflects the new line
    // (component-cart-items morphs in place when detail.data.sections is present,
    // else falls back to an ASYNC server re-render). The synchronous morph matters:
    // awp_cart-drawer wires its free-spoon reconciler (evaluateSpoon) on a
    // microtask after cart:update, and it reads awp_cart_has_subscription from the
    // freshly-morphed section dataset. Without the sections the async fallback lands
    // too late — evaluateSpoon reads the stale (pre-add) snapshot, sees no
    // subscription, and the first-order free spoon is never added. Mirrors
    // coffee-quiz.js and the PDP add (awp_product_form.js).
    const cartSectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (
        el instanceof HTMLElement &&
        el.dataset.sectionId &&
        cartSectionIds.indexOf(el.dataset.sectionId) === -1
      ) {
        cartSectionIds.push(el.dataset.sectionId);
      }
    });
    if (cartSectionIds.length > 0) {
      params.append('sections', cartSectionIds.join(','));
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          Accept: '*/*',
        },
        body: params.toString(),
      });

      const data = await response.json();
      if (!response.ok || data?.status) {
        throw new Error(data?.description || data?.message || 'Add to cart failed');
      }

      this.close();

      // Refresh + open the native cart drawer. Pass the rendered sections from the
      // add response so cart-items-component morphs the drawer IN PLACE and
      // synchronously (falling back to a server re-render only if sections are
      // absent) — the synchronous morph is what lets awp_cart-drawer's spoon
      // reconciler read a fresh awp_cart_has_subscription and add the free spoon.
      // Then open the drawer explicitly so the new line shows immediately. Same
      // flow the coffee quiz uses.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: { source: 'awp-quick-add', sections: data?.sections } },
        })
      );
      const drawer =
        document.querySelector('cart-drawer-component') || document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    } catch (error) {
      console.error('AWP Quick Add: cart add error', error);
      if (submit) {
        submit.disabled = false;
        submit.textContent = 'Añadir al carrito';
      }
      if (this.bodyEl && !this.bodyEl.querySelector('[data-awp-qa-add-error]')) {
        const err = document.createElement('div');
        err.className = 'awp-qa__error';
        err.setAttribute('data-awp-qa-add-error', '');
        err.textContent = 'No pudimos añadir al carrito. Inténtalo de nuevo.';
        this.bodyEl.appendChild(err);
      }
    } finally {
      this.#isAdding = false;
    }
  }

  #selectedFrequency() {
    const sel = this.bodyEl?.querySelector('[data-awp-qa-frequency]');
    return sel ? sel.value : AWP_QA_DEFAULT_FREQUENCY;
  }
}

if (!customElements.get('awp-quick-add-dialog')) {
  customElements.define('awp-quick-add-dialog', AwpQuickAddDialog);
}

/** Minimal HTML-attribute/text escape for values interpolated into innerHTML. */
function awpQaEscape(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
